import type { ActionCapability } from './personalActionManifest.ts';
import { parseCalendarEventOptions, calendarAttendeeEmail, type CalendarEventOptions } from './calendarDomain.ts';

export const OPERATOR_CALENDAR_INTENT_FIELDS = ['guest_emails', 'repeat_frequency', 'repeat_interval', 'repeat_count', 'calendar_intent'] as const;
export function calendarIntentOptions(input: Record<string, unknown>, existing?: CalendarEventOptions): CalendarEventOptions | undefined {
  const has = (key: string) => Object.hasOwn(input, key);
  if (!OPERATOR_CALENDAR_INTENT_FIELDS.slice(0, 4).some(has)) return undefined;
  const base = existing ?? { attendees: [], recurrence: null, scope: 'single', notify_attendees: false };
  let attendees = base.attendees;
  if (has('guest_emails')) {
    const value = input.guest_emails;
    const emails = typeof value === 'string' ? [value] : value;
    if (!Array.isArray(emails) || emails.length > 20) throw new Error('invalid_calendar_attendee');
    attendees = emails.map(email => ({ email: calendarAttendeeEmail(email) }));
  }
  let recurrence = base.recurrence;
  if (has('repeat_frequency')) {
    const frequency = input.repeat_frequency;
    if (frequency === 'none') recurrence = null;
    else {
      const integer = (key: string, fallback: number) => {
        if (!has(key)) return fallback;
        if (typeof input[key] !== 'string' || !/^[1-9][0-9]{0,2}$/.test(input[key] as string)) throw new Error('invalid_calendar_recurrence');
        return Number(input[key]);
      };
      recurrence = { frequency: frequency as 'daily', interval: integer('repeat_interval', 1), count: integer('repeat_count', 4) };
    }
  } else if (has('repeat_interval') || has('repeat_count')) throw new Error('invalid_calendar_recurrence');
  const scope = base.scope === 'occurrence' ? 'occurrence' : recurrence ? 'series' : 'single';
  return parseCalendarEventOptions({ attendees, recurrence, scope, notify_attendees: false }, false);
}
export function calendarReviewAcknowledgements(cap: ActionCapability, payload: Record<string, unknown>): string[] {
  if (cap.adapter !== 'calendar' || !cap.approval || payload.event_options === undefined) return [];
  const options = parseCalendarEventOptions(payload.event_options);
  return ['calendar_options_acknowledged', ...(options.notify_attendees ? ['notify_attendees'] : []), ...(options.scope === 'series' ? ['series_acknowledged'] : [])];
}
export function sameCalendarOptions(left: CalendarEventOptions, right: CalendarEventOptions): boolean {
  const recipients = (options: CalendarEventOptions) => options.attendees.map(row => row.email.toLowerCase()).sort().join('\n');
  return left.scope === right.scope && JSON.stringify(left.recurrence) === JSON.stringify(right.recurrence) && recipients(left) === recipients(right);
}
